import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import GrillaEffect from 'views/Grillas/GrillaEffect';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useHistory } from 'react-router-dom';

const RegistrosConciliados = () => {
  const defaultFecha = [
    { id: 'fecha_movimiento', value: [dayjs().subtract(100, 'days'), dayjs(new Date())] }
  ];
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState([...defaultFecha]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const history = useHistory();

  const showToast = (title, description = '', severity = 'info') => {
    setSnackbar({ open: true, message: description ? `${title}: ${description}` : title, severity });
  };

  const handleRevertir = async (id) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/tesoreriacomprobantes/items/registros/revertir-conciliacion/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': localStorage.getItem('token'),
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.error === 'El registro no está conciliado.') {
          showToast('Información', 'El registro ya ha sido revertido.', 'info');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } else {
        showToast('Conciliación revertida', `El estado de conciliación del registro con ID ${id} fue modificado.`, 'success');
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error revirtiendo la conciliación:', error);
      showToast('Error', 'No se pudo cambiar el estado de conciliación del registro.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmRevertir = (id) => {
    setConfirmDialog({ open: true, id });
  };

  const handleConfirmRevertir = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: null });
    await handleRevertir(id);
  };

  const handleRevertirMultiple = async () => {
    if (selectedRows.length === 0) return;
    setIsLoading(true);
    try {
      for (const row of selectedRows) {
        await handleRevertir(row.id_registro);
      }
      setSelectedRows([]);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // toast ya manejado en handleRevertir
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters([...defaultFecha]);
    setRefreshTrigger(prev => prev + 1);
    showToast('Filtros limpiados', 'Se han eliminado todos los filtros aplicados excepto el rango de fechas.', 'info');
  };

  const NoResultsMessage = () => (
    <Stack spacing={2} sx={{ p: 4, textAlign: 'center', alignItems: 'center' }}>
      <SearchOffIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography variant="h6" color="text.secondary">
        No se encontraron registros conciliados con los filtros aplicados
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Intenta ajustar los criterios de búsqueda o limpiar los filtros
      </Typography>
      <Button startIcon={<ClearAllIcon />} variant="outlined" onClick={handleClearFilters}>
        Limpiar filtros adicionales
      </Button>
    </Stack>
  );

  const customTopToolbar = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <IconButton
        color="primary"
        disabled={selectedRows.length === 0}
        onClick={handleRevertirMultiple}
        title="Revertir Seleccionados"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderRadius: 1 }}
      >
        <DoneAllIcon /> Revertir Seleccionados
      </IconButton>
      <IconButton
        color="secondary"
        disabled={filters.length <= 1}
        onClick={handleClearFilters}
        title="Limpiar Filtros"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderRadius: 1 }}
      >
        <ClearAllIcon /> Limpiar Filtros
      </IconButton>
      <Button
        variant="contained"
        color="secondary"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => history.push('/admin/conciliarops')}
      >
        Volver a Conciliar OPS
      </Button>
      <Typography variant="body2" color="text.secondary">
        {selectedRows.length > 0 ? `${selectedRows.length} seleccionados` : ''}
        {filters.length > 1 && ` | ${filters.length - 1} filtros adicionales`}
      </Typography>
    </Box>
  );

  const onRowSelectionChange = (rowSelection, table) => {
    const selected = Object.keys(rowSelection)
      .filter(rowId => rowSelection[rowId])
      .map(rowId => table.getRowModel().rows.find(r => r.id === rowId)?.original)
      .filter(Boolean);
    setSelectedRows(selected);
  };

  const columnNames = {
    fecha_movimiento: 'Fecha Pago',
    id_comprobante_extbc: 'ID EXTBC',
    id_comprobante_lops: 'ID LOPS',
    concepto_descripcion: 'CTA BCO',
    razonSocial: 'Razon Social',
    num_comprobante: 'N° Cheque/N° Transferencia',
    importe: 'Importe',
    id_registro: 'ID Reg. LOPS',
    acciones: 'Acciones',
  };

  const colsGrillaHidden = { id_registro: false };

  return (
    <Box sx={{ p: 2, pt: '120px' }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        {data.length === 0 && !isLoading && filters.length > 1 ? (
          <NoResultsMessage />
        ) : (
          <GrillaEffect
            key={refreshTrigger}
            data={data}
            updateData={setData}
            filters={filters}
            updateFilters={setFilters}
            colsHeaders={columnNames}
            hiddenColumns={colsGrillaHidden}
            method="GET"
            baseURI={`${process.env.REACT_APP_API_URL}/tesoreriacomprobantes/items/registros-conciliados`}
            handleRevertir={confirmRevertir}
            customTopToolbar={customTopToolbar}
            onRowSelectionChange={onRowSelectionChange}
            isLoading={isLoading}
            displayColumnDefOptions={{
              'mrt-row-actions': {
                size: 100,
                muiTableHeadCellProps: { align: 'center' },
              },
            }}
            rowSelectionEnabled={true}
          />
        )}
      </LocalizationProvider>

      {/* Dialog de confirmación (reemplaza toast con render custom) */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, id: null })}>
        <DialogTitle>Confirmar Acción</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Desea revertir la conciliación del registro con ID {confirmDialog.id}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, id: null })}>Cancelar</Button>
          <Button onClick={handleConfirmRevertir} variant="contained" color="primary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RegistrosConciliados;
